import { useState } from 'react';
import { Alert, Button, Flex, Loader, Paper, Stack, Text } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import type { SmartCreateData } from 'src/secret-menu/smart-create/types';
import { useSmartCreateEnvelope } from '../hooks/useSmartCreateEnvelope';

export default function ContractReviewStep({
  data,
  setData,
  nextStep,
  previousStep,
  canGoBack,
}: StepProps<SmartCreateData>) {
  const [error, setError] = useState<string | null>(null);
  const { sendEnvelope, isPending } = useSmartCreateEnvelope();

  async function handleSend() {
    setError(null);
    try {
      const result = await sendEnvelope({
        companyName: data.companyName!,
        companyNumber: data.companyNumber!,
        registeredAddress: data.registeredAddress!,
        signatoryName: `${data.firstName} ${data.lastName}`,
        signatoryEmail: data.email,
        signatoryPosition: data.signatoryPosition ?? '',
        trialEndDate: new Date(data.trialEndDate!).toISOString().split('T')[0],
      });
      setData(prev => ({ ...prev, envelopeId: result.data.envelopeId }));
      nextStep();
    } catch {
      setError('Failed to send contract. Please try again.');
    }
  }

  const trialEndFormatted = data.trialEndDate
    ? new Date(data.trialEndDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—';

  return (
    <Flex direction="column" flex={1} gap="md" mih={0}>
      <Text size="displaySm" fw={FONT_WEIGHTS.medium} c="gray.10">
        Review contract
      </Text>

      <Stack flex={1} gap="sm" style={{ overflowY: 'auto' }} mih={0}>
        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={FONT_WEIGHTS.medium} size="textSm" c="gray.6">
              Company
            </Text>
            <Text fw={FONT_WEIGHTS.medium}>{data.companyName}</Text>
            <Text c="gray.7">Company number: {data.companyNumber}</Text>
            <Text c="gray.7">{data.registeredAddress}</Text>
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={FONT_WEIGHTS.medium} size="textSm" c="gray.6">
              Signatory
            </Text>
            <Text fw={FONT_WEIGHTS.medium}>
              {data.firstName} {data.lastName}
            </Text>
            <Text c="gray.7">{data.email}</Text>
            {data.signatoryPosition && <Text c="gray.7">{data.signatoryPosition}</Text>}
          </Stack>
        </Paper>

        <Paper withBorder p="md" radius="md">
          <Stack gap="xs">
            <Text fw={FONT_WEIGHTS.medium} size="textSm" c="gray.6">
              Terms
            </Text>
            <Text c="gray.7">Trial end date: {trialEndFormatted}</Text>
            <Text c="gray.7">Initial term: no charge</Text>
            <Text c="gray.7">
              Early adopter (to 31 Dec 2026): £2.50/month per family group (min £50/month)
            </Text>
            <Text c="gray.7">Thereafter: £5/month per family group (min £100/month)</Text>
          </Stack>
        </Paper>

        {error && <Alert color="red">{error}</Alert>}
      </Stack>

      <Stack gap="sm">
        <Flex justify="space-between" align="center" gap="md">
          {canGoBack && (
            <Button variant="outline-black" size="xl" onClick={() => previousStep()} p="sm">
              <IconArrowLeft size={24} />
            </Button>
          )}
          <Button
            fullWidth
            size="xl"
            onClick={handleSend}
            disabled={isPending}
            leftSection={isPending ? <Loader size="sm" color="gray.4" /> : undefined}
          >
            Send contract
          </Button>
        </Flex>
      </Stack>
    </Flex>
  );
}
